// ABOUTME: Consent config for the storefront — categories, versioning, copy, and the
// ABOUTME: canCaptureAudio predicate that gates microphone capture on explicit consent.

// Bump this whenever the disclosure/privacy terms change: a stored record with a
// different version is treated as "no decision" and the user is asked to consent again.
//
// DELIBERATELY NOT BUMPED for the 2026-09-02 copy correction below (team call). That edit fixed a
// false statement — the old wording said we RECORD the microphone; we do not, the audio is streamed
// to Gemini Live and discarded as it is processed (`services/stream_controller.py` in the agent
// repo puts the PCM straight into the live queue and never writes it to disk, a bucket or a
// database). Normally that is exactly the kind of change this constant exists to re-prompt for.
//
// It is not bumped because nothing has gone live on production yet: there is no guest anywhere
// holding a consent record against the old wording, so there is nothing to invalidate and nobody
// to re-ask. The one visible oddity is cosmetic — the policy overlay stamps this date above copy
// written later.
//
// 2026-09-09 (owner): copy updated again, still pre-launch and still not bumped for the same reason —
// the policy now names who is responsible (the brand from /api/config as controller, Versai as
// processor, Google Cloud as sub-processor in europe-west1), states the real retention periods
// (conversation text ≤ 1 hour, saved pieces ≤ 30 days — the deployed RUNTIME_SNAPSHOT_TTL_S and
// SELECTION_SNAPSHOT_TTL_S), drops "check out" (no checkout exists yet — add it with SPEC-46), and the
// launcher no longer says "cookies" (we set none). The contact address is still the brand's to give.
//
// Once production is live, any further change to the disclosure or policy text DOES need a bump.
// This is the only thing this constant gates: `useConsent` writes it onto the stored record and
// rejects a record that does not match, and the overlay displays it. Nothing else reads it —
// `visitMemory` is deliberately independent, so a bump never resurrects the intro film.
//
// 2026-09-15: BUMPED. Production has been live since 2026-09-01
// (ophion.prod.versai.world), so this is the first policy-text change to which the comment above
// actually applies. The change adds back a "Payment" disclosure now that SPEC-46 (Stripe checkout)
// exists in code: card details are entered on Stripe's own hosted page and never reach our
// servers (`services/checkout_service.py`, D-2); the guest's email and order details come back to
// us via Stripe's webhook and are held in a Redis marker for 30 days
// (`services/checkout_store.py`, `checkout:paid:{sid}`) so the purchase can be confirmed and the
// house notified (`services/order_notify.py`). `STRIPE_CHECKOUT_ENABLED` is still `"false"` in
// every Ophion overlay as of this date, but the mechanism exists in the shipped code and the
// policy should describe it truthfully regardless of the flag.
//
// 2026-10-08: deliberately NOT bumped (owner decision) although the gate's disclosure was rewritten into one text and its
// accept became "accept all" (voice + analytics). A record made on the old granular screen stays valid: it states exactly
// what that guest chose, and nothing new is inferred for it (analytics is collected by nothing today). A bump would re-prompt
// every live guest once for no change in what they agreed to.
// 2026-10-15: BUMPED (owner). The text now matches what is built: a written transcript of every
// conversation (what the guest said, as transcribed, and what the assistant replied) is kept in Google
// Cloud logs for up to 30 days and reviewed by the team to improve the boutique (`USER SAYS` / `AGENT
// SAYS` in services/stream_controller.py; prj-p-versai-production-3f87 _Default bucket, 30-day retention);
// technical logs (IP address, browser, pages requested) are kept 30 days; the waitlist form's details are
// emailed to the brand (services/waitlist_intake.py, WAITLIST_NOTIFY_TO) and no longer logged (backend
// branch fix/redact-inbound-contact-log); the waitlist-consent check sends the guest's words to Gemini via
// Google's `global` endpoint (services/waitlist_consent_judge.py DEFAULT_LOCATION); Stripe's form is
// embedded in the boutique (ui_mode="embedded", services/checkout_service.py). Review 2026-10-15 (owner rulings): typed
// text is transcribed whatever the voice choice (stream_controller `Received text message`); transcripts are also copied
// into internal daily reports purged after 30 days (ophion_daily_report.py); the waitlist no-copy line names the logs, so
// it stays true before and after the redaction fix and while pre-fix log entries age out. Every live guest is asked once more.
export const CONSENT_VERSION = '2026-10-15';

// localStorage key. Matches the existing versai_* client-storage naming (versai_token,
// versai_wishlist_*). See App.js wishlist persistence for the read/write idiom.
export const CONSENT_STORAGE_KEY = 'versai_consent';

// Consent categories. `necessary` is always on and cannot be toggled; the others are
// opt-in and default to off (GDPR: no pre-ticked boxes).
export const CATEGORIES = [
  {
    id: 'necessary',
    label: 'Strictly necessary',
    locked: true,
    default: true,
    description:
      'Your sign-in session, the pieces you save, this privacy choice, and whether you’ve seen the opening film. Kept in your browser — we use no cookies. Always on: the boutique cannot work without these.',
  },
  {
    // The id stays `audioRecording` even though the label no longer says "recording": it is the key
    // every stored consent record and `canCaptureAudio` reads. Renaming it would be a data
    // migration for no user-visible gain (decided with the team, 2026-09-02).
    id: 'audioRecording',
    label: 'Voice conversation',
    locked: false,
    default: false,
    description:
      'Lets the assistant hear you. Your audio is streamed live to Google’s Gemini (Google Cloud, EU region) and never recorded. Spoken or typed, a transcript is kept up to 30 days (see the privacy policy). Required for voice; decline and you can still type.',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    locked: false,
    default: false,
    description:
      'Anonymous measurement in your browser of how the boutique is used, to improve it. None is collected today; this preference applies if it is ever switched on.',
  },
];

// Default category state: necessary on, everything optional off.
// mic_voice_control (dev + staging, owner 2026-10-08): the in-app Privacy & consent panel carries no voice row - the microphone
// button is the one voice control (one tap turns voice on and records it), "voice off for good" is the browser's site setting.
export const PANEL_CATEGORIES = CATEGORIES.filter((c) => c.id !== 'audioRecording');

export const DEFAULT_CATEGORIES = CATEGORIES.reduce((acc, c) => {
  acc[c.id] = c.default;
  return acc;
}, {});

// Placeholder plain-language copy. Swap the wording for legal-approved text when available.
export const DISCLOSURE = {
  title: 'Your privacy in the boutique',
  // Owner 2026-10-08: the entry gate is ONE consolidated text and two buttons (accept all / reject all) — the three category boxes
  // and their checkboxes are gone from it; per-category choice lives in the in-app Privacy & consent panel. Same facts as the
  // CATEGORIES descriptions above (each checked against the agent repo); DRAFT wording, legal to confirm like the rest.
  intro:
    'This private salon has a voice assistant. Accept, and your browser will ask to use your microphone: what you say is streamed live through our servers to Google’s Gemini model (Google Cloud, EU region) so the assistant can understand and answer you. The audio is processed as you speak and never recorded. Whether you speak or type, a written transcript of the conversation is kept for up to 30 days so we can fix problems and improve the boutique. Your browser keeps what the boutique needs to work: your session, saved pieces, this choice and whether you have seen the opening film. We use no cookies. Anonymous analytics are not collected today; accepting covers them if they are ever switched on. Reject, and you can still browse and type. Change any of this at any time under Privacy & consent.',
  privacyLinkLabel: 'Read our privacy policy',
  acceptLabel: 'Accept all & enter',
  rejectLabel: 'Reject all & enter',
  manageTitle: 'Privacy & data preferences',
  saveLabel: 'Save preferences',
  cancelLabel: 'Cancel',
  // "Privacy & consent", not "& cookies": the boutique sets no cookies (the categories say so), and
  // the label was the one place that implied otherwise (owner question, 2026-09-09).
  launcherLabel: 'Privacy & consent',
  voiceOffTitle: 'Your concierge is muted',
  voiceOffBody: 'Enable voice to talk to your AI concierge.',
  enableVoiceLabel: 'Enable voice',
  // The no_enter_gate return landing with voice declined: the one tap turns voice ON (owner 2026-10-08), so it says what that
  // means - the same facts as the gate.
  turnOnVoiceBody: 'Turn on voice: what you say is streamed live to Google’s Gemini (EU region); the audio is never recorded.',
  turnOnVoiceLabel: 'Turn on voice',
  // Voice consent given, but the BROWSER refused the microphone (useMicBlocked). `micHardBody` is for a browser that will not ask
  // again: only the guest can lift it, from the site's permission settings.
  micOffTitle: 'Your microphone is off',
  micOffBody: 'Allow microphone access to talk to your AI concierge.',
  // A blocked mic: only the guest can lift it, from the browser's own site settings - so the copy is the exact steps per device.
  micBlockedTitle: 'Microphone blocked',
  micHardBody: 'Click the icon at the left of the address bar and set Microphone to Allow.',
  micHardBodyIos: 'Tap aA in the address bar → Website Settings → Microphone → Allow.',
  micHardBodyAndroid: 'Tap the icon at the left of the address bar → Permissions → Microphone → Allow.',
  micUnavailableBody: 'No microphone was found. Check one is connected.',
  micFailedBody: 'The microphone could not start. Close other apps using it, then try again.',
  enableMicLabel: 'Enable microphone',
  // no_enter_gate return landing: consent is on file and the mic may already be allowed — the tap just starts the conversation.
  // Owner 2026-10-08: not "ready" - nothing is connected until this tap.
  startTitle: 'Talk to your concierge',
  startBody: 'Tap to start the conversation.',
  startLabel: 'Start conversation',
  retryMicLabel: 'Try again',
};

// In-app privacy policy copy. Plain-language and production-shaped, but DRAFT: the exact
// wording and the contact details must be confirmed by legal before launch. Rendered by
// PrivacyPolicy.js as a scrollable overlay opened from the disclosure link.
//
// The copy is tenant-neutral: `{brand}` is filled from /api/config's `brand_name` through
// `policyFor()` below (SPEC-30: brand is data, never a literal in shared code). Every factual claim
// here is checked against the agent repo — audio is never written anywhere
// (`services/stream_controller.py` streams PCM straight to the live queue), the conversation session
// lives ≤ 1 hour (`RUNTIME_SNAPSHOT_TTL_S`, `services/session_store.py`), saved pieces ≤ 30 days
// (`SELECTION_SNAPSHOT_TTL_S`, `services/session_snapshot.py`), the voice model runs in europe-west1
// (`GOOGLE_CLOUD_LOCATION`), and analytics is off in every deployed overlay.
export const BRAND_TOKEN = '{brand}';
export const PRIVACY_POLICY = {
  title: 'Privacy policy',
  updated: `Version ${CONSENT_VERSION}`,
  sections: [
    {
      heading: 'What we collect',
      body: 'When the microphone is on, we take your speech as you say it, and we keep a written transcript of the conversation — what you said or typed and what the assistant replied. If you share a photo, we take that photo. If you join a waitlist, we take the name, email address, phone number and note you enter in the form. If you complete a purchase, we receive your order details and confirmation of payment from Stripe, our payment processor — never your card number. Like any website, our servers also log technical information about each visit: your IP address, your browser and device type, and the pages and files requested. We also keep a few things in your browser: your sign-in session, the pieces you’ve saved, your privacy choices, and whether you’ve seen the opening film. We use no cookies and no third-party tracking.',
    },
    {
      heading: 'Your voice',
      body: 'Your microphone audio is streamed to our servers and on to Google’s Gemini model, running on Google Cloud Vertex AI in the European Union (Belgium, europe-west1), so the assistant can understand you and reply. It is processed as it arrives and then discarded — we do not record it, write it to disk, or keep an audio file of any kind. The words you say are also used to search this boutique’s catalogue and archive on Google Cloud. When you answer an offer to join a waitlist, your reply is also checked by a separate Gemini model to confirm you really asked to join; that check uses Google’s global service, so that short text may be processed outside the European Union under Google Cloud’s data-protection terms. The microphone is off until you turn it on, and turning it off stops the stream immediately.',
    },
    {
      heading: 'Photos you share',
      body: 'If you share a photo to see a piece styled on you, it is held in memory on our server only for as long as it takes to compose the image, then discarded. It is never written to disk or added to a database. The styled image, and any short film generated from it, are sent straight back to your browser and are not stored by us.',
    },
    {
      heading: 'Waitlists',
      body: 'If you join a waitlist, the details you enter — name, email address, phone number and any note — are sent by email to {brand} so it can contact you about the piece, and we email you a confirmation. {brand} holds them in its own records; we keep no separate copy beyond our technical logs and internal reports, which are deleted automatically within 30 days.',
    },
    {
      heading: 'Payment',
      body: 'If you choose to buy a piece, you enter your card details in Stripe’s own secure payment form, shown inside the boutique — we never see or store your card number. Stripe shares your email address and order details back with us so we can confirm your purchase and pass it to {brand} for fulfilment; we keep that record for up to 30 days, then it is deleted. Stripe processes payment under its own privacy policy, as an independent controller of the payment itself.',
    },
    {
      heading: 'Why we process it',
      body: 'To run the conversation: to understand what you ask, answer it, and help you browse and save pieces. To keep the boutique secure and working, and to improve it: our team reviews conversation transcripts to fix problems and improve the assistant, and counts visits in aggregate. What you say and share is not used to train AI models, is not sold, and is not passed to advertisers.',
    },
    {
      heading: 'Who is responsible',
      body: 'This boutique is offered by {brand}, which decides how your information is used. Versai builds and runs the boutique’s software for {brand} and processes your information only on its instructions. Google Cloud provides the voice model, the catalogue search and our logs as Versai’s sub-processor, and an email delivery provider sends waitlist and confirmation messages.',
    },
    {
      heading: 'What we keep, and for how long',
      body: 'While you are here we hold a short-lived session — including the text of the conversation — so it survives a dropped connection; it expires automatically within one hour. Separately, the written transcript of the conversation and our technical logs (including your IP address and browser details) are kept in secured Google Cloud logs, and transcripts also in internal daily reports our team reviews, for up to 30 days, then deleted automatically. The pieces you save are kept on our servers for up to 30 days so they are there when you return, and in your browser until you clear it. A completed order is kept for up to 30 days so it can be confirmed and fulfilled, then deleted. Sign-in codes expire in ten minutes. Your voice is never kept at all.',
    },
    {
      heading: 'Your rights',
      body: 'You can review, change or withdraw any optional permission at any time from the “Privacy & consent” control. Turning voice off stops the microphone immediately. You can also ask for a copy of anything we hold about you, or ask us to delete it.',
    },
    {
      heading: 'Contact',
      // `{brand_contact}` is the brand's own public address from /api/config (`privacy_contact`);
      // Versai's processor contact is the same for every tenant (owner 2026-09-09).
      body: 'For any privacy request or question about this boutique, contact {brand} at {brand_contact}. Versai, which processes your information on {brand}’s behalf, can be reached at jean-christophe@zilliqa.com.',
    },
  ],
};

export const BRAND_CONTACT_TOKEN = '{brand_contact}';
const BRAND_CONTACT_FALLBACK = 'the address given on its website';

/**
 * The policy with `{brand}` and `{brand_contact}` filled in — the object the overlay renders.
 *
 * @param {string} [brandName] `brand_name` from /api/config; blank or missing falls back to
 *   "the house", so a tenant whose config failed to load still reads as a sentence.
 * @param {string} [brandContact] `privacy_contact` from /api/config; blank falls back to a neutral
 *   phrase rather than another brand's address.
 */
// mic_voice_control (dev + staging): voice is not a panel row there, so 'Your rights' says where voice is switched instead.
export const RIGHTS_MIC_VOICE_CONTROL = 'You can review, change or withdraw any optional permission at any time from the “Privacy & consent” control. Voice is controlled from the microphone button: tap it to mute the microphone for this visit. To turn voice off for good, block the microphone for this site in your browser’s settings — the boutique then never captures audio. You can also ask for a copy of anything we hold about you, or ask us to delete it.';

export const policyFor = (brandName, brandContact, { micVoiceControl = false } = {}) => {
  const brand = (brandName || '').trim() || 'the house';
  const contact = (brandContact || '').trim() || BRAND_CONTACT_FALLBACK;
  return {
    ...PRIVACY_POLICY,
    sections: PRIVACY_POLICY.sections.map((section) => ({
      ...section,
      body: (micVoiceControl && section.heading === 'Your rights' ? RIGHTS_MIC_VOICE_CONTROL : section.body)
        .split(BRAND_TOKEN).join(brand).split(BRAND_CONTACT_TOKEN).join(contact),
    })),
  };
};

// The keystone invariant: audio may be captured only when the audioRecording category
// has been explicitly granted. Tolerates null/undefined/malformed consent (returns false).
export const canCaptureAudio = (consent) =>
  consent?.categories?.audioRecording === true;
